import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, SettingsGroup, SettingsItemAutocomplete } from '@elipssolution/harfang';
import { styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import SettingsDialogPage from '../../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../../hooks/useSettingsDialog';
import { CustomerFileType } from '../../../../../../types/customerFile';
import { generateErrorInformations } from '../../../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../../utils/statusAnimationDelay';
import { FETCH_PAYMENT_MODES_BY_CUSTOMER_FILE, FetchPaymentModesByCustomerFileType } from '../../../../api/paymentMode';
import {
	CREATE_CUSTOMER_FILE_BILL_EXCHANGE_SETTING_PAYMENT_MODE,
	CREATE_CUSTOMER_FILE_CHECK_SETTING_PAYMENT_MODE,
	CreateCustomerFileBillExchangeSettingPaymentModeType,
	CreateCustomerFileCheckSettingPaymentModeType,
	FETCH_CUSTOMER_FILE_BILL_EXCHANGE_SETTING_PAYMENT_MODE,
	FETCH_CUSTOMER_FILE_CHECK_SETTING_PAYMENT_MODE,
	FetchCustomerFileBillExchangeSettingPaymentModeType,
	FetchCustomerFileCheckSettingPaymentModeType,
} from '../../../../api/settingPaymentMode';
import { CheckTypeEnum } from '../../../../types/check';
import { PaymentModeType } from '../../../../types/paymentMode';

const ActionWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

const ErrorWrapper = styled('div')(({ theme: { palette, shape } }) => ({
	height: 36,
	width: '50%',

	display: 'grid',
	placeItems: 'center',

	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	borderRadius: shape.borderRadius,
}));

type FormFieldType = {
	billExchangePaymentMode?: PaymentModeType;
	checkIssuancePaymentMode?: PaymentModeType;
	checkRemittancePaymentMode?: PaymentModeType;
};

type PaymentModesProps = {
	customerFileId?: CustomerFileType['id'];
};

const PaymentModes = ({ customerFileId }: PaymentModesProps) => {
	const { onHasDirtyFormChange } = useSettingsDialog();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		setValue,
		reset,
	} = useForm<FormFieldType>();

	const [error, setError] = useState<string>();

	const isFormValid = useMemo(() => isDirty && isValid, [isDirty, isValid]);

	const [fetchPaymentModesByCustomerFile] = useLazyQuery<FetchPaymentModesByCustomerFileType>(
		FETCH_PAYMENT_MODES_BY_CUSTOMER_FILE,
	);

	const paymentModesDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: PaymentModeType[];
		}> => {
			const { data, error: queryError } = await fetchPaymentModesByCustomerFile({
				variables: {
					customerFileId,
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (queryError) {
				throw queryError;
			}

			const {
				paymentModesByCustomerFile: { count = 0, items = [] },
			} = data ?? {
				paymentModesByCustomerFile: {},
			};

			return { count, items };
		},
		[fetchPaymentModesByCustomerFile, customerFileId],
	);

	const { data: fetchedCustomerFileBillExchangeSettingPaymentModeData } =
		useQuery<FetchCustomerFileBillExchangeSettingPaymentModeType>(
			FETCH_CUSTOMER_FILE_BILL_EXCHANGE_SETTING_PAYMENT_MODE,
			{
				onCompleted: ({ quickentry_settingCustomerFileBillExchangePaymentMode: { paymentMode } }) =>
					setValue('billExchangePaymentMode', paymentMode ?? undefined),
				onError: (queryError) =>
					setError(
						generateErrorInformations({
							error: queryError,
							resource: 'quickentry_settingCustomerFileBillExchangePaymentMode',
						}).message,
					),
				skip: !customerFileId,
				variables: { id: customerFileId },
			},
		);

	const { data: fetchedCustomerFileCheckIssuanceSettingPaymentModeData } =
		useQuery<FetchCustomerFileCheckSettingPaymentModeType>(FETCH_CUSTOMER_FILE_CHECK_SETTING_PAYMENT_MODE, {
			onCompleted: ({ quickentry_settingCustomerFileCheckPaymentMode: { paymentMode } }) =>
				setValue('checkIssuancePaymentMode', paymentMode ?? undefined),
			onError: (queryError) =>
				setError(
					generateErrorInformations({
						error: queryError,
						resource: 'quickentry_settingCustomerFileCheckPaymentMode',
					}).message,
				),
			skip: !customerFileId,
			variables: { id: customerFileId, checkType: CheckTypeEnum.CHECK_ISSUANCE },
		});

	const { data: fetchedCustomerFileCheckRemittanceSettingPaymentModeData } =
		useQuery<FetchCustomerFileCheckSettingPaymentModeType>(FETCH_CUSTOMER_FILE_CHECK_SETTING_PAYMENT_MODE, {
			onCompleted: ({ quickentry_settingCustomerFileCheckPaymentMode: { paymentMode } }) =>
				setValue('checkRemittancePaymentMode', paymentMode ?? undefined),
			onError: (queryError) =>
				setError(
					generateErrorInformations({
						error: queryError,
						resource: 'quickentry_settingCustomerFileCheckPaymentMode',
					}).message,
				),
			skip: !customerFileId,
			variables: { id: customerFileId, checkType: CheckTypeEnum.CHECK_REMITTANCE },
		});

	const { quickentry_settingCustomerFileBillExchangePaymentMode } =
		fetchedCustomerFileBillExchangeSettingPaymentModeData || {};
	const { paymentMode: billExchangePaymentMode, inheritedPaymentMode: inheritedBillExchangePaymentMode } =
		quickentry_settingCustomerFileBillExchangePaymentMode || {};

	const { quickentry_settingCustomerFileCheckPaymentMode: settingCustomerFileCheckIssuancePaymentMode } =
		fetchedCustomerFileCheckIssuanceSettingPaymentModeData || {};
	const { paymentMode: checkIssuancePaymentMode, inheritedPaymentMode: inheritedCheckIssuancePaymentMode } =
		settingCustomerFileCheckIssuancePaymentMode || {};

	const { quickentry_settingCustomerFileCheckPaymentMode: settingCustomerFileCheckRemittancePaymentMode } =
		fetchedCustomerFileCheckRemittanceSettingPaymentModeData || {};
	const { paymentMode: checkRemittancePaymentMode, inheritedPaymentMode: inheritedCheckRemittancePaymentMode } =
		settingCustomerFileCheckRemittancePaymentMode || {};

	const [
		createCustomerFileBillExchangePaymentMode,
		{ data: createCustomerFileBillExchangePaymentModeData, reset: resetCreateCustomerFileBillExchangePaymentMode },
	] = useMutation<CreateCustomerFileBillExchangeSettingPaymentModeType>(
		CREATE_CUSTOMER_FILE_BILL_EXCHANGE_SETTING_PAYMENT_MODE,
		{
			onCompleted: ({ quickentry_createSettingCustomerFileBillExchangePaymentMode: { paymentMode } }) =>
				setValue('billExchangePaymentMode', paymentMode ?? undefined),
		},
	);

	const [
		createCustomerFileCheckIssuancePaymentMode,
		{ data: createCustomerFileCheckIssuancePaymentModeData, reset: resetCreateCustomerFileCheckIssuancePaymentMode },
	] = useMutation<CreateCustomerFileCheckSettingPaymentModeType>(CREATE_CUSTOMER_FILE_CHECK_SETTING_PAYMENT_MODE, {
		onCompleted: ({ quickentry_createSettingCustomerFileCheckPaymentMode: { paymentMode } }) =>
			setValue('checkIssuancePaymentMode', paymentMode ?? undefined),
	});

	const [
		createCustomerFileCheckRemittancePaymentMode,
		{
			data: createCustomerFileCheckRemittancePaymentModeData,
			reset: resetCreateCustomerFileCheckRemittancePaymentMode,
		},
	] = useMutation<CreateCustomerFileCheckSettingPaymentModeType>(CREATE_CUSTOMER_FILE_CHECK_SETTING_PAYMENT_MODE, {
		onCompleted: ({ quickentry_createSettingCustomerFileCheckPaymentMode: { paymentMode } }) =>
			setValue('checkRemittancePaymentMode', paymentMode ?? undefined),
	});

	const hasMutationSucceeded = useMemo(
		() =>
			!!createCustomerFileBillExchangePaymentModeData ||
			!!createCustomerFileCheckIssuancePaymentModeData ||
			!!createCustomerFileCheckRemittancePaymentModeData,
		[
			createCustomerFileBillExchangePaymentModeData,
			createCustomerFileCheckIssuancePaymentModeData,
			createCustomerFileCheckRemittancePaymentModeData,
		],
	);

	const onSubmit = useCallback(
		(values: FormFieldType) => {
			const {
				billExchangePaymentMode: formBillExchangePaymentMode,
				checkIssuancePaymentMode: formCheckIssuancePaymentMode,
				checkRemittancePaymentMode: formCheckRemittancePaymentMode,
			} = values;

			const submitActions = [];

			if (formBillExchangePaymentMode?.id !== billExchangePaymentMode?.id) {
				const { id: formBillExchangePaymentModeId } = formBillExchangePaymentMode ?? {};

				submitActions.push(
					createCustomerFileBillExchangePaymentMode({
						variables: {
							createSettingCustomerFileBillExchangePaymentModeInput: {
								customerFileId,
								paymentModeId: formBillExchangePaymentModeId ?? undefined,
							},
						},
					}),
				);
			}

			if (formCheckIssuancePaymentMode?.id !== checkIssuancePaymentMode?.id) {
				const { id: formCheckIssuancePaymentModeId } = formCheckIssuancePaymentMode ?? {};

				submitActions.push(
					createCustomerFileCheckIssuancePaymentMode({
						variables: {
							createSettingCustomerFileCheckPaymentModeInput: {
								customerFileId,
								paymentModeId: formCheckIssuancePaymentModeId ?? undefined,
								checkType: CheckTypeEnum.CHECK_ISSUANCE,
							},
						},
					}),
				);
			}

			if (formCheckRemittancePaymentMode?.id !== checkRemittancePaymentMode?.id) {
				const { id: formCheckRemittancePaymentModeId } = formCheckRemittancePaymentMode ?? {};

				submitActions.push(
					createCustomerFileCheckRemittancePaymentMode({
						variables: {
							createSettingCustomerFileCheckPaymentModeInput: {
								customerFileId,
								paymentModeId: formCheckRemittancePaymentModeId ?? undefined,
								checkType: CheckTypeEnum.CHECK_REMITTANCE,
							},
						},
					}),
				);
			}

			(async () => {
				await Promise.all(submitActions);

				reset({
					billExchangePaymentMode: formBillExchangePaymentMode,
					checkIssuancePaymentMode: formCheckIssuancePaymentMode,
					checkRemittancePaymentMode: formCheckRemittancePaymentMode,
				});

				setTimeout(() => {
					resetCreateCustomerFileBillExchangePaymentMode();
					resetCreateCustomerFileCheckIssuancePaymentMode();
					resetCreateCustomerFileCheckRemittancePaymentMode();
				}, SUCCESS_ANIMATION_DELAY);
			})().catch((e) => {
				throw e;
			});
		},
		[
			billExchangePaymentMode?.id,
			checkIssuancePaymentMode?.id,
			checkRemittancePaymentMode?.id,
			createCustomerFileBillExchangePaymentMode,
			createCustomerFileCheckIssuancePaymentMode,
			createCustomerFileCheckRemittancePaymentMode,
			customerFileId,
			reset,
			resetCreateCustomerFileBillExchangePaymentMode,
			resetCreateCustomerFileCheckIssuancePaymentMode,
			resetCreateCustomerFileCheckRemittancePaymentMode,
		],
	);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
	}, [isDirty, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title="Modes de règlement">
			<SettingsGroup>
				<Controller
					control={control}
					name="checkIssuancePaymentMode"
					render={({ field: { onChange, ...field } }) => {
						const handleChange = (changedValue?: PaymentModeType | null) => onChange(changedValue);

						return (
							<SettingsItemAutocomplete<PaymentModeType>
								{...field}
								dataSource={paymentModesDataSource}
								getOptionLabel={({ code, name: journalName }) => `${code} - ${journalName}`}
								label='Mode de règlement "Chèques émis"'
								onChange={handleChange}
								inheritedValue={inheritedCheckIssuancePaymentMode}
								disableClearable
							/>
						);
					}}
				/>
				<Controller
					control={control}
					name="checkRemittancePaymentMode"
					render={({ field: { onChange, ...field } }) => {
						const handleChange = (changedValue?: PaymentModeType | null) => onChange(changedValue);

						return (
							<SettingsItemAutocomplete<PaymentModeType>
								{...field}
								dataSource={paymentModesDataSource}
								getOptionLabel={({ code, name: journalName }) => `${code} - ${journalName}`}
								label='Mode de règlement "Remise de chèques"'
								onChange={handleChange}
								inheritedValue={inheritedCheckRemittancePaymentMode}
								disableClearable
							/>
						);
					}}
				/>
				<Controller
					control={control}
					name="billExchangePaymentMode"
					render={({ field: { onChange, ...field } }) => {
						const handleChange = (changedValue?: PaymentModeType | null) => onChange(changedValue);

						return (
							<SettingsItemAutocomplete<PaymentModeType>
								{...field}
								dataSource={paymentModesDataSource}
								getOptionLabel={({ code, name: journalName }) => `${code} - ${journalName}`}
								label='Mode de règlement "LCR émises"'
								onChange={handleChange}
								inheritedValue={inheritedBillExchangePaymentMode}
								disableClearable
							/>
						);
					}}
				/>
			</SettingsGroup>

			<ActionWrapper>
				<Button disabled={!isFormValid} onClick={handleSubmit(onSubmit)} variant="contained">
					{hasMutationSucceeded ? 'Modes de règlement validés' : 'Valider'}
				</Button>

				{error && <ErrorWrapper>{error}</ErrorWrapper>}
			</ActionWrapper>
		</SettingsDialogPage>
	);
};

export default PaymentModes;
